import { z } from "zod";

/**
 * Answer Policy — the declarative, config-as-data definition of how an answer
 * blends + attributes its source tiers (Library / AI / Web). This is the
 * "behavior plane" of the source-attribution architecture: a sysadmin edits a
 * ROW; a thin in-code interpreter (AnswerPolicyFilter) reads it and renders the
 * prompt. NO prose/precedence/refusal lives in pipeline code anymore.
 *
 * DESIGN PRINCIPLE — CLOSED & ENUMERATED. Every field here is typed and finite.
 * The interpreter understands a FIXED set of conditions (knowledge mode, tier
 * presence, intent, ITAR). This is deliberately NOT a Turing-complete rules DSL:
 * the escape hatch for genuinely novel conditional logic is a signed compute
 * function (the "transform plane"), never an ever-growing condition tree in
 * JSON. Adding a free-form conditional field here is a smell — push it into a
 * computeHook or extend the enumerated condition set in code.
 *
 * BYTE-IDENTICAL CUTOVER. The seeded global default policy carries
 * `persona` + `intentGuidance` copied VERBATIM from the legacy
 * SystemPromptFilter (BASE_PERSONA + getIntentInstruction). With the
 * attribution-specific fields (`tierGuidance`, the refusal templates) left at
 * their byte-neutral defaults, the interpreter reproduces today's prompt
 * exactly; the new attribution behavior is then turned on by EDITING THE ROW
 * (a data change, golden-set-gated), not a code change.
 */

// ── Source tier registry ──────────────────────────────────────────────────
// ONE source of truth for both the prompt render AND the frontend legend.

export const TierIdSchema = z.enum(["library", "ai", "web"]);
export type TierId = z.infer<typeof TierIdSchema>;

export const PolicyTierSchema = z.object({
  id: TierIdSchema,
  /** Display label — "Library" / "AI" / "Web". */
  label: z.string(),
  /** Verified = faithfulness-checked customer knowledge (library). ai/web = false. */
  verified: z.boolean(),
  /** UI color token: 'blue' (library) | 'amber' (ai) | 'teal' (web). */
  color: z.enum(["blue", "amber", "teal"]),
  /** Legend / hover copy. */
  description: z.string().default(""),
  /** Sub-source kinds the Library umbrella spans (doc/wiki/connector/mcp). */
  includesSubSources: z.array(z.string()).default([]),
});
export type PolicyTier = z.infer<typeof PolicyTierSchema>;

// ── Per-tier blend guidance (attribution prose) ────────────────────────────
// Empty strings at cutover (byte-neutral); filled in by a row edit to turn on
// the visible attribution behavior.

export const TierGuidanceSchema = z.object({
  library: z.string().default(""),
  ai: z.string().default(""),
  web: z.string().default(""),
});

// ── Knowledge-mode behavior (the enumerated condition set) ─────────────────

export const OnLibrarySilentSchema = z.enum(["refuse", "answer_from_ai_labeled"]);

export const KnowledgeModeBehaviorSchema = z.object({
  library_only: z.object({
    /** What to do when the Library returns nothing. 'refuse' = the honest path. */
    onLibrarySilent: OnLibrarySilentSchema.default("refuse"),
    /** Exact refusal instruction injected when the library is silent. */
    refusalTemplate: z.string().default(""),
    allowAiFallback: z.boolean().default(false),
    allowWebFallback: z.boolean().default(false),
  }),
  library_plus_ai: z.object({
    onLibrarySilent: OnLibrarySilentSchema.default("answer_from_ai_labeled"),
    /** Require the model to label which tier each part of the answer came from. */
    requireTierLabel: z.boolean().default(true),
  }),
});

// ── Compute hooks (the transform plane — signed function references) ────────
// Optional per-policy references to signed compute functions that reshape an
// arbitrary payload into a typed Citation (the ONLY imperative escape hatch).

export const ComputeHooksSchema = z
  .object({
    /** functions.id of a signed fn turning an MCP tool result into a Library citation. */
    mcpResultToCitation: z.string().uuid().nullable().default(null),
    /** functions.id of a signed fn normalizing a web_search result into a Web citation. */
    webResultNormalizer: z.string().uuid().nullable().default(null),
  })
  .default({ mcpResultToCitation: null, webResultNormalizer: null });

// ── The full policy content (stored as answer_policies.content jsonb) ───────

export const AnswerPolicyContentSchema = z.object({
  /** Content schema version — bump on breaking shape changes; interpreter pins it. */
  schemaVersion: z.literal(1),

  /** The Library/AI/Web tier registry. */
  tiers: z.array(PolicyTierSchema).min(1),

  /** Base persona — rendered at the FRONT of the prompt (legacy BASE_PERSONA). */
  persona: z.string(),

  /**
   * Per-intent instruction (legacy getIntentInstruction). Keyed by intent verdict
   * (rag_only | hybrid | mcp_only | tool_use | direct_answer | conversational);
   * a `default` key is the fallback. Rendered at the front, after the persona.
   */
  intentGuidance: z.record(z.string(), z.string()),

  /** Attribution blend prose per tier — rendered into the terminal policy slot. */
  tierGuidance: TierGuidanceSchema.default({ library: "", ai: "", web: "" }),

  /** Precedence when tiers conflict: prefer earlier. */
  precedence: z.array(TierIdSchema).default(["library", "ai", "web"]),

  /** Conflict resolution rule (enumerated). */
  conflictRule: z.enum(["prefer_library"]).default("prefer_library"),

  /** Knowledge-mode behavior (library_only honesty + library_plus_ai blend). */
  knowledgeModeBehavior: KnowledgeModeBehaviorSchema,

  /** Optional signed compute-function references. */
  computeHooks: ComputeHooksSchema,
});
export type AnswerPolicyContent = z.infer<typeof AnswerPolicyContentSchema>;

// ── Binding (which scope a policy row applies to) ──────────────────────────

export const PolicyBindingSchema = z.object({
  /** Resolution scope. Resolution order is model > tenant > global. */
  scope: z.enum(["global", "tenant", "model"]),
  /** Tenant (organization) UUID for scope='tenant'; null otherwise. */
  tenantId: z.string().uuid().nullable().default(null),
  /** Model brand id (e.g. 'argus_pro') for scope='model'; null otherwise. */
  modelId: z.string().nullable().default(null),
});
export type PolicyBinding = z.infer<typeof PolicyBindingSchema>;
